import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRootRoute, Link, Outlet, useRouter, useRouterState } from '@tanstack/react-router'
import { Flame } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { AiAssistant } from '../components/ai-assistant'
import { ChatNotifications } from '../components/chat-notifications'
import { NotificationCenter } from '../components/notification-center'
import { PwaInstallSheet } from '../components/pwa-install-sheet'
import { RueckwegLeiste } from '../components/rueckweg-leiste'
import { MobileShellBottom } from '../components/shell/mobile-shell-bottom'
import { MobileShellTop } from '../components/shell/mobile-shell-top'
import { Sidebar } from '../components/sidebar'
import { ConfirmDialogHost } from '../components/ui/confirm-dialog'
import { API_BASE_URL, api, onUnauthorized } from '../lib/api'
import { onAuthChange } from '../lib/auth-broadcast'
import { MAIL_SCOPE_STORAGE_KEY } from '../lib/mail-account-scope'
import {
  lastOfflineUser,
  mailOfflineFactory,
  purgeForeignUsers,
  purgeMailOfflineForUser,
} from '../lib/mail-offline-store'
import { useNotificationStore } from '../lib/notification-store'
import { isNetworkError, onlineStatus } from '../lib/online-status'
import {
  detectInstallPlatform,
  getDismissedAt,
  markAutoShownThisSession,
  shouldShowInstallSheet,
  wasAutoShownThisSession,
} from '../lib/pwa-install'
import { configureSessionEnd, endSession, reportUnauthorized } from '../lib/session-end'
import { useShellStore } from '../lib/shell-store'
import { useIsMobile, useIsTabletBand } from '../lib/use-media-query'

export const Route = createRootRoute({
  component: RootLayout,
})

const COLLAPSED_KEY = 'embertime.sidebar.collapsed'

/** api.me() answers null for a 4xx AND after repeated network errors; only a
 * real answer tells them apart: 401 = session gone, fetch error = offline. Any
 * other 4xx (a proxy, WAF or rate limit in front of the API) is not a verdict on
 * the session, so it never purges the outbox. */
export async function probeSession(): Promise<'gone' | 'offline' | 'ok'> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
      credentials: 'include',
      cache: 'no-store',
    })
    return res.status === 401 ? 'gone' : 'ok'
  } catch (err) {
    return isNetworkError(err) ? 'offline' : 'ok'
  }
}

/** Everything but the auth state leaves the query cache (#785): the data keys
 * carry no user, so a previous user's mails must not be shown or stored offline
 * under the next one. In-flight requests are cancelled first. */
async function dropUserQueries(queryClient: QueryClient): Promise<void> {
  const predicate = (query: { queryKey: readonly unknown[] }) => query.queryKey[0] !== 'auth'
  await queryClient.cancelQueries({ predicate })
  queryClient.removeQueries({ predicate })
}

/** Session state outside the query cache (#834): in-app notifications and the
 * remembered mail scope (#535, an account id of the old user). */
function clearSessionStores(): void {
  useNotificationStore.getState().clear()
  try {
    window.localStorage.removeItem(MAIL_SCOPE_STORAGE_KEY)
  } catch {
    // storage blocked: nothing remembered
  }
}

function RootLayout() {
  return (
    <>
      <RueckwegLeiste />
      <AppLayout />
    </>
  )
}

function AppLayout() {
  const queryClient = useQueryClient()
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
    // 60s-Poll, damit eine Rollen-Änderung (z.B. Sidebar-Sichtbarkeit)
    // ohne Reload bei allen aktiven Sessions ankommt.
    refetchInterval: 60_000,
    // Innerhalb eines Tabs den frisch geladenen Auth-State kurz als frisch
    // betrachten, damit Navigation nicht sofort neu fetcht (und bei einem
    // Hiccup auf /login springt). Cross-Tab-Sync läuft über onAuthChange.
    staleTime: 30_000,
  })

  // Session end (#834): one path for logout, a 401 of any API, a logout in
  // another tab and a gone session — cache, stores and offline data go, then
  // straight to /login.
  const router = useRouter()
  useEffect(() => {
    const unconfigure = configureSessionEnd({
      queryClient,
      purge: (userId) => purgeMailOfflineForUser(userId),
      lastUser: () => lastOfflineUser(),
      clearStores: clearSessionStores,
      navigateToLogin: async () => {
        try {
          await router.navigate({ to: '/login', replace: true })
        } catch {
          window.location.replace(`${import.meta.env.BASE_URL}login`)
        }
      },
      currentPath: () => router.state.location.pathname,
      probe: probeSession,
    })
    const offUnauthorized = onUnauthorized((status, path) => {
      void reportUnauthorized(status, path)
    })
    return () => {
      offUnauthorized()
      unconfigure()
    }
  }, [queryClient, router])

  // Cross-Tab-Sync: Login/Logout in einem anderen Tab invalidiert hier sofort
  // den Auth-State (Logout propagiert instant, statt erst beim 60s-Poll).
  useEffect(() => {
    return onAuthChange((type) => {
      // Logout elsewhere: the session is gone here too (same cookie) — the
      // mail offline store + outbox (#785) go and the tab leaves for /login.
      if (type === 'logout') {
        void endSession('broadcast')
        return
      }
      void (async () => {
        // Login (maybe another user): no late answer of the old session may
        // land in the cache or in an offline store.
        await dropUserQueries(queryClient).catch(() => undefined)
        queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      })()
    })
  }, [queryClient])

  // Offline mail data (#785): another user logged in → the previous user's
  // database and every foreign one go. Session gone (me = null, confirmed by a
  // 4xx) → the last user's data goes. A network error never purges.
  const meUserId = me?.user?.id ?? null
  const meKnownEmpty = me === null
  // Last user seen in this tab: a different one → the query cache is dropped.
  const lastUserRef = useRef<string | null>(null)
  useEffect(() => {
    if (meUserId) {
      const seen = lastUserRef.current
      lastUserRef.current = meUserId
      if (seen && seen !== meUserId) void dropUserQueries(queryClient).catch(() => undefined)
      const previous = lastOfflineUser()
      void (async () => {
        if (previous && previous !== meUserId) await purgeMailOfflineForUser(previous)
        await purgeForeignUsers(mailOfflineFactory(), meUserId)
      })().catch(() => undefined)
      return
    }
    if (!meKnownEmpty) return
    const previous = lastOfflineUser()
    // Nothing to clean up when no user was ever seen here or stored offline.
    if (!previous && !lastUserRef.current) return
    let cancelled = false
    void probeSession().then((state) => {
      if (cancelled) return
      if (state === 'gone') {
        // The session's data leaves the cache, the offline data the device,
        // the tab goes to /login (#834).
        void endSession('unauthorized', previous)
      } else if (state === 'offline') onlineStatus.reportNetworkError()
    })
    return () => {
      cancelled = true
    }
  }, [meUserId, meKnownEmpty, queryClient])
  const location = useRouterState({ select: (s) => s.location.pathname })
  // /g = öffentliche Gastseite für Meeting-Gastlinks (#751) im Login-Layout
  const isAuthRoute = location === '/login' || location === '/register' || location === '/g'
  const isWelcomePage = location === '/welcome'
  // Shell-State (Drawer + Notification-Center) lebt im shell-store, damit
  // auch die Mobile-Shell-Bars (<640) ☰/🔔 auslösen können. Verhalten hier
  // identisch zu den bisherigen lokalen useState.
  const drawerOpen = useShellStore((s) => s.drawerOpen)
  const closeDrawer = useShellStore((s) => s.closeDrawer)
  const notifOpen = useShellStore((s) => s.notifOpen)
  const openNotif = useShellStore((s) => s.openNotif)
  const closeNotif = useShellStore((s) => s.closeNotif)
  const [installSheetOpen, setInstallSheetOpen] = useState(false)
  const isPhone = useIsMobile()
  const isTabletBand = useIsTabletBand()
  const loggedIn = !!me?.user
  // Generische Mobile-Shell-Bars (Phone hoch+quer): auf JEDER eingeloggten
  // Route — seit W4 portalt auch der Kalender in die Shell-Outlets. Login/
  // Register/Welcome erreichen diesen Zweig nie (early returns oben).
  const showShellBars = isPhone && loggedIn
  // Tablet-Band 640–1023 (T2, „Rail + Bar"): Landscape-Phones matchen die
  // Band-Query AUCH (844 liegt in 640–1023) — dort gewinnt die Phone-Shell,
  // deshalb der !isPhone-Guard. railMode erzwingt die Icon-Rail (collapsed,
  // ohne Toggle), showTabletBar rendert NUR die Bottom-Bar (☰ → Drawer),
  // KEINE MobileShellTop — Seiten zeigen im Band ihre Desktop-Köpfe.
  const railMode = isTabletBand && !isPhone
  const showTabletBar = railMode && loggedIn

  // PWA-Install-Sheet: nach Login am Phone im Browser-Tab automatisch
  // anbieten — mit 2s Delay (nicht reinplatzen), max. 1× pro Tab-Session,
  // und mit Snooze nach „Später". Plattform wird erst IM Timeout bestimmt,
  // damit ein spät eintreffendes beforeinstallprompt noch mitzählt.
  useEffect(() => {
    if (!loggedIn || !isPhone) return
    // Auf Welcome/Auth-Routen ist das Sheet nicht gemountet — dort weder
    // zeigen noch das 1×-pro-Session-Budget verbrauchen.
    if (isWelcomePage || isAuthRoute) return
    if (wasAutoShownThisSession()) return
    const t = window.setTimeout(() => {
      const platform = detectInstallPlatform(true)
      if (shouldShowInstallSheet({ platform, dismissedAt: getDismissedAt(), now: Date.now() })) {
        markAutoShownThisSession()
        setInstallSheetOpen(true)
      }
    }, 2000)
    return () => window.clearTimeout(t)
  }, [loggedIn, isPhone, isWelcomePage, isAuthRoute])
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: location triggers re-close on navigate
  useEffect(() => {
    closeDrawer()
  }, [location])

  // Persist server-side preference if logged in
  const updateCollapsedServer = useMutation({
    mutationFn: (val: boolean) => api.updateProfile({ sidebarCollapsed: val }),
  })

  const onToggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    try {
      window.localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0')
    } catch {
      // ignore quota errors
    }
    if (me?.user) updateCollapsedServer.mutate(next)
  }

  // Welcome page renders without sidebar/app chrome
  if (isWelcomePage) {
    return (
      <>
        <Outlet />
        <ConfirmDialogHost />
      </>
    )
  }

  if (isAuthRoute) {
    return (
      <>
        <main className="app-safe-auth min-h-[calc(100vh-var(--rueckweg-height,0px))] flex flex-col items-center justify-center px-6 py-12">
          <Link
            to="/login"
            className="mb-8 flex items-center gap-2.5 text-text-primary hover:text-accent transition-colors"
          >
            <Flame className="h-6 w-6 text-accent" strokeWidth={2.25} />
            <span className="brand-mark text-2xl">Ember</span>
          </Link>
          <Outlet />
          <p className="mt-8 text-xs text-text-tertiary">selfhosted Zeiterfassung</p>
        </main>
        <ConfirmDialogHost />
      </>
    )
  }

  return (
    <>
      {/* h-dvh (NICHT h-screen): 100vh ist der GROSSE Viewport — bei
          sichtbarer Mobile-URL-Leiste gilt 100vh > innerHeight, der body wird
          um die Differenz (~56–110px) dokument-scrollbar und die ganze App
          lässt sich als zweiter Scroller verschieben (iOS Safari ignoriert
          overscroll-contain beim Chaining). 100dvh schrumpft mit der
          Browser-Chrome → html-Excess ist in jedem URL-Leisten-Zustand 0 und
          nur die inneren Grid-Scroller scrollen. Desktop identisch
          (vh == dvh ohne dynamische Chrome).
          --app-height (lib/app-height.ts, Boot in main.tsx): iOS-Standalone
          initialisiert 100dvh beim Kaltstart teils stale (Bar hing „mittig",
          bis man wischte) — die JS-gepflegte Var folgt dem sichtbaren
          Viewport; Fallback bleibt 100dvh. */}
      {/* data-shell-root: Marker für den scrollY-0-Reset in lib/app-height.ts
          (nur die overflow-hidden-Shell, nicht Auth/Welcome). */}
      <div
        data-shell-root
        className="h-[calc(var(--app-height,100dvh)-var(--rueckweg-height,0px))] flex overflow-hidden"
      >
        {/* Sidebar-Spalte ab 640 (sm:flex — vorher md:flex, das Band ersetzt
            auch 640–767, wo bisher GAR keine Sidebar stand):
              · 640–1023 (railMode): erzwungene Icon-Rail w-14 — collapsed
                ohne Toggle (hideCollapseToggle), COLLAPSED_KEY bleibt
                unberührt, ≥1024 gilt wieder die gespeicherte Präferenz.
              · ≥1024: wie bisher — localStorage-collapsed + Toggle,
                byte-identisch (railMode ist dort false).
            phone:hidden: Landscape-Phones (844×390) sind ≥sm BREIT, aber
            per Definition Phone (useIsMobile) — dort navigiert die Shell
            (☰ in der Bottom-Bar), die Rail bliebe sonst als
            Doppel-Navigation stehen. */}
        <aside
          className={`hidden sm:flex phone:hidden shrink-0 flex-col border-r border-border-subtle bg-bg-elevated/40 transition-[width] duration-200 sidebar-root ${
            railMode || collapsed ? 'w-14' : 'w-56 lg:w-60'
          }`}
        >
          <Sidebar
            collapsed={railMode || collapsed}
            onToggleCollapsed={onToggleCollapsed}
            onNotifClick={openNotif}
            hideCollapseToggle={railMode}
          />
        </aside>

        {/* Mobile-/Band-Drawer — lg:hidden (vorher md:hidden): im Tablet-Band
            640–1023 öffnet das ☰ der Bottom-Bar den Drawer mit vollen
            Labels/Gruppen (die Rail hat nur Icons). phone:block: im
            Handy-Querformat (Breite ≥640) bliebe sonst ein via ☰ geöffneter
            Drawer unsichtbar; die phone:-Variante steht im Build-CSS nach
            lg: und gewinnt. */}
        {drawerOpen && (
          <div className="fixed inset-0 z-40 lg:hidden phone:block">
            <button
              type="button"
              aria-label="Sidebar schließen"
              className="absolute inset-0 bg-bg-base/80 backdrop-blur-sm"
              onClick={closeDrawer}
            />
            <aside
              className="relative flex h-full w-72 flex-col border-r border-border-subtle bg-bg-elevated animate-fade-in mobile-drawer-sidebar"
              // Standalone-PWA: Drawer-Inhalt unter der Notch rausziehen —
              // additiv, im Browser-Tab ist env() = 0.
              style={{ paddingTop: 'var(--app-safe-top)', paddingLeft: 'var(--app-safe-left)' }}
            >
              {/* Kein X mehr oben — der Schließen-Toggle sitzt jetzt unten
                  links neben der User-Anzeige (siehe Sidebar onClose).
                  hideCollapseToggle: der Drawer kann nicht einklappen
                  (onToggleCollapsed ist ein No-op) — ohne das Prop stünde ab
                  768 Breite (Band/Landscape-Phone, Toggle-CSS md:flex) ein
                  toter „einklappen"-Button im Drawer. */}
              <Sidebar
                collapsed={false}
                onToggleCollapsed={() => undefined}
                hideCollapseToggle
                onItemClick={closeDrawer}
                onClose={closeDrawer}
                onInstallClick={() => {
                  closeDrawer()
                  setInstallSheetOpen(true)
                }}
              />
            </aside>
          </div>
        )}

        {/* main ist eine Flex-SPALTE: [MobileShellTop?] → der EINE Scroller
            → [MobileShellBottom?]. Die Bars sind Flex-Kinder (KEIN
            position:fixed) — der Scroller endet physisch an der Bar-Kante,
            Seiten brauchen keine dvh-Höhenklemmen mehr (h-full genügt).
            Tablet-Band 640–1023: nur Bottom-Bar, keine Top-Leiste — die
            alte mitscrollende 640–767-Topbar ist mit T2 gelöscht, Seiten
            zeigen ihre Desktop-Köpfe (≥640 sichtbar), Brand + Bell wohnen
            in der Rail. ≥1024 rendert keine Bar → Scrollverhalten wie
            zuvor, nur dass der Scroller das innere div statt main ist. */}
        <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {showShellBars && <MobileShellTop />}

          {/* Der EINE Scroller. Trägt .main-content (index.css):
              overflow-x-Sperre auf Phones + standalone-safe-area ≥1024. */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain main-content shell-safe-inline">
            {/* Page container — unconstrained width so dashboards and lists
                fill 1920px+ monitors; individual pages add their own max-width
                for text-heavy content (settings forms cap at max-w-3xl).

                <640 zusätzlich h-full: gibt dem Container die definite
                Scroller-Höhe, damit App-Seiten (chat/kanban/mail) mit
                `h-full min-h-0` exakt den sichtbaren Bereich füllen können
                (statt der toten dvh-Klemmen). Längere Seiten überlaufen den
                Container einfach und scrollen wie bisher. ≥640 byte-identisch. */}
            <div className={`w-full px-3 sm:px-4 lg:px-6 py-3 lg:py-4${isPhone ? ' h-full' : ''}`}>
              <Outlet />
            </div>
          </div>

          {/* Bottom-Bar: Phones (Shell) ODER Tablet-Band. Seiten-Slots
              registrieren seit T3 in beiden Welten (MobileBottomSlots →
              useShellBars); ohne Registrierung Minimal-Bar (☰ + Freiraum). */}
          {(showShellBars || showTabletBar) && <MobileShellBottom />}
        </main>
      </div>
      <ConfirmDialogHost />
      <ChatNotifications />
      <NotificationCenter open={notifOpen} onClose={closeNotif} />
      <PwaInstallSheet open={installSheetOpen} onClose={() => setInstallSheetOpen(false)} />
      <AiAssistant />
    </>
  )
}
